import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { Dispatch, PropsWithChildren, SetStateAction, useState } from 'react';

import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import { ActionIcon } from '@cineamo/ui/src/button/ActionIcon';
import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';

type BaseModalProps = PropsWithChildren<{
    closeable?: boolean;
    showCloseButton?: boolean;
    /** `null` suppresses the default close (router.back()); `undefined` keeps it. */
    onClose?: (() => void) | null;
    beforeClose?: () => boolean | void;
}>;

export type ModalProps =
    | ({
          modalIsOpen: boolean;
          setModalIsOpen: Dispatch<SetStateAction<boolean>>;
      } & BaseModalProps)
    | ({
          modalIsOpen?: undefined;
          setModalIsOpen?: undefined;
      } & BaseModalProps);

const FullScreenModal: React.FC<ModalProps> = (props) => {
    const { onClose, beforeClose } = props;

    const t = useTranslations();

    const router = useRouter();

    let modalIsOpen = !!(props?.modalIsOpen ?? false);
    let setModalIsOpen = props?.setModalIsOpen;
    const [state, setState] = useState<boolean>(true);

    if (modalIsOpen === undefined && setModalIsOpen === undefined) {
        modalIsOpen = state;
        setModalIsOpen = setState;
    }

    const closeable = props.closeable !== false;

    function onCloseInternal() {
        if (beforeClose?.() === false) {
            return;
        }
        setModalIsOpen?.(false);
        if (onClose) {
            onClose();
        } else if (onClose === undefined) {
            router.back();
        }
    }

    if (modalIsOpen === false) {
        return null;
    }

    return (
        <Dialog
            open={modalIsOpen}
            onClose={() => {
                if (closeable) {
                    onCloseInternal();
                }
            }}
            className="fixed z-modal-view inset-0 w-screen h-full">
            <DialogBackdrop className="fixed inset-0 flex items-center justify-center w-full h-full backdrop-blur-lg bg-black/80" />

            <div className="fixed inset-0 flex w-screen items-center justify-center">
                {closeable && props.showCloseButton !== false && (
                    <div className="absolute right-8 top-8 md:top-24 md:right-24">
                        <ActionIcon
                            icon={
                                <IconClose24
                                    fill={tailwindColors.white['80']}
                                    className="opacity-60"
                                />
                            }
                            action={onCloseInternal}
                            tooltipText={t('common.close')}
                        />
                    </div>
                )}
                <DialogPanel className="flex flex-col p-0 items-center">{props.children}</DialogPanel>
            </div>
        </Dialog>
    );
};

export default FullScreenModal;
